<template>
  <section class="help-page">
    <div class="role-name text-center mt-3" :class="roleClass">
      {{ tr.yourRole }}{{ roleName }}
    </div>

    <div class="help-content mt-3"></div>

    <div v-for="(item, index) in helpItems" :key="index">
      <button class="toggle-btn mt-2" type="button" @click="toggle(index)">
        <span>{{ item.title }}</span>
        <i class="fa" :class="openedIndex === index ? 'fa-angle-up' : 'fa-angle-down'"></i>
      </button>

      <div v-if="openedIndex === index" class="card-text">
        <div v-html="item.text"></div>
        <div class="alert-message p-2 text-center">
          {{ tr.notAnswered }}
        </div>
      </div>
    </div>

    <div class="alert-message mt-3 p-2 text-center">
      {{ tr.alert_message_warning_login }}
    </div>

    <div class="d-flex justify-content-center mt-3">
      <button class="submit f-13" type="button" @click="goBack">
        <i class="fa fa-angle-left me-1"></i>
        {{ tr.back_button_label }}
      </button>
    </div>

    <div
      v-if="isCordovaApp && firebaseToken"
      class="token-hotspot"
      @click="revealAndCopyToken"
      aria-hidden="true"
    ></div>

    <div v-if="tokenVisible && firebaseToken" class="token-box">
      <div v-if="tokenCopied" class="token-status">Copied</div>
      <pre class="token-value">{{ firebaseToken }}</pre>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { useDeviceStore } from '@/stores/device';
import { useLangStore } from '@/stores/lang';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

const router = useRouter();
const deviceStore = useDeviceStore();
const langStore = useLangStore();
const topbarStore = useTopbarStore();
const viewerStore = useViewerStore();
const openedIndex = ref(null);
const tokenVisible = ref(false);
const tokenCopied = ref(false);

const tr = computed(() => langStore.translations || {});
const helpItems = computed(() => (Array.isArray(tr.value.helpContent) ? tr.value.helpContent : []));
const roleClass = computed(() => (viewerStore.role === 'child' ? 'child-role' : 'parent-role'));
const roleName = computed(() => (viewerStore.role === 'child' ? tr.value.role_child : tr.value.role_parent));
const isCordovaApp = computed(() => Boolean(deviceStore.isCordovaReady || window?.cordova));
const firebaseToken = computed(() => String(deviceStore.phoneToken || localStorage.getItem('phoneToken') || ''));

function toggle(index) {
  openedIndex.value = openedIndex.value === index ? null : index;
}

function goBack() {
  router.go(-1);
}

async function copyToClipboard(text) {
  const value = String(text || '');
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {}

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

async function revealAndCopyToken() {
  tokenVisible.value = true;
  tokenCopied.value = await copyToClipboard(firebaseToken.value);
}

onMounted(() => {
  topbarStore.setTitle(tr.value.sidebar_help_label || '');
});
</script>

<style scoped>
.help-page {
  padding: 0 16px 96px;
}

@media screen and (min-width: 768px) {
  .help-page {
    padding: 0 24px 120px;
  }
}

.help-content {
  border-bottom: 1px solid #e0e0e0;
}

.card-text {
  border: none;
  padding: 8px 4px;
  border-radius: 0 0 3px 3px;
  background: #fff;
}

.alert-message {
  color: #f2994a;
  background: #fff4e8;
  border: 1px solid #fde2c5;
  border-radius: 3px;
  font-size: 12px;
}

.submit {
  color: #fff;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.24);
  border-radius: 3px;
  border: none;
  background-color: #36b453;
  padding: 6px 14px;
}

.toggle-btn {
  width: 100%;
  border-bottom: 1px dashed #d1d1d1;
  border-left: none;
  border-top: none;
  border-right: none;
  font-size: 14px;
  border-radius: 3px;
  color: #000;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  text-align: left;
}

.role-name {
  padding: 5px;
  border-radius: 3px;
  font-size: 14px;
}

.token-hotspot {
  width: 100%;
  height: 40px;
  margin-top: 12px;
  cursor: default;
  opacity: 0;
}

.token-box {
  margin-top: 8px;
  padding: 10px 12px;
  background: #f5f6f7;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.token-status {
  margin-bottom: 8px;
  color: #36b453;
  font-size: 12px;
  font-weight: 500;
}

.token-value {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-wrap: anywhere;
  color: #1b1c1c;
  font-size: 12px;
  line-height: 18px;
}

.parent-role {
  background: #e9f5ff;
  color: #00a6de;
  border: 1px solid #caeafe;
}

.child-role {
  background: #ecf9ee;
  color: #36b453;
  border: 1px solid #d9f2df;
}
</style>
